import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, REGIMES_TAGS, inscriptionSchema, type Etape, type NombreFoyer } from '@dinertournant/core';
import { BandeauAlerte, BandeauSignature, Champ, Embleme, IconeCommune, IconeEtape, IconeMaison, PastilleEtape } from '../../components/index.js';
import { ErreurApi, api, capitale, dateCourte, heureCourte, plageEtape, type EvenementPublic, type FoyerPublic } from '../../lib/api.js';

type RegimeTag = (typeof REGIMES_TAGS)[number];

export interface ValeursInscription {
  nombre: NombreFoyer;
  personne1: string;
  personne2?: string;
  personne3?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  telephone?: string;
  adresse: string;
  complementAdresse?: string;
  regimes: RegimeTag[];
  precisionsRegime?: string;
  veutRecevoir: boolean;
  disponibilites: Etape[];
  capaciteMax?: number | undefined;
  consentement?: boolean;
}

const valeursVides: ValeursInscription = {
  nombre: 1,
  personne1: '',
  personne2: '',
  personne3: '',
  email1: '',
  email2: '',
  email3: '',
  telephone: '',
  adresse: '',
  complementAdresse: '',
  regimes: [],
  precisionsRegime: '',
  veutRecevoir: false,
  disponibilites: [],
  capaciteMax: 6,
  consentement: true,
};

function depuisFoyer(f: FoyerPublic): ValeursInscription {
  return {
    nombre: Math.min(3, Math.max(1, f.membres.length)) as NombreFoyer,
    personne1: f.membres[0] ?? '',
    personne2: f.membres[1] ?? '',
    personne3: f.membres[2] ?? '',
    email1: f.emails?.[0] ?? (f.membres.length === 1 ? (f.email ?? '') : ''),
    email2: f.emails?.[1] ?? '',
    email3: f.emails?.[2] ?? '',
    telephone: f.telephone ?? '',
    adresse: f.adresse ?? '',
    complementAdresse: f.complementAdresse ?? '',
    regimes: f.regimes.filter((r): r is RegimeTag => (REGIMES_TAGS as readonly string[]).includes(r)),
    precisionsRegime: f.precisionsRegime ?? '',
    veutRecevoir: f.veutRecevoir,
    disponibilites: f.disponibilites,
    capaciteMax: f.capaciteMax ?? 6,
    consentement: true,
  };
}

/** Phrase de l'encart « Vous recevrez une seule fois… », mise à jour en direct (aria-live). */
export function phraseReception(dispos: Etape[]): string {
  const ordre = ETAPES.filter((e) => dispos.includes(e));
  if (ordre.length === 0) return 'Cochez au moins une étape : vous ne recevrez qu’une seule fois.';
  const noms = ordre.map((e) => ETAPE_INFO[e].au);
  const liste = noms.length === 1 ? noms[0]! : `${noms.slice(0, -1).join(', ')} ou ${noms.at(-1)}`;
  const suite = ordre.length === 1 ? 'Aux deux autres étapes, vous serez invités.' : "L'organisateur vous dira laquelle. Aux deux autres étapes, vous serez invités.";
  return `Vous recevrez une seule fois : ${liste}. ${suite}`;
}

interface Props {
  mode: 'inscription' | 'modification';
}

/** Écran 2 : inscription participant (page publique mobile), fidèle à captures/2-inscription.png. */
export function Inscription({ mode }: Props) {
  const { slug, token } = useParams<{ slug: string; token: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EvenementPublic | null>(null);
  const [chargement, setChargement] = useState<'en_cours' | 'ok' | 'introuvable'>('en_cours');
  const [erreurGlobale, setErreurGlobale] = useState<string | null>(null);
  const [modifiable, setModifiable] = useState(true);
  const [raisonBlocage, setRaisonBlocage] = useState<'cloture' | 'repartition' | null>(null);
  const refPrecisions = useRef<HTMLInputElement | null>(null);

  const form = useForm<ValeursInscription>({
    resolver: zodResolver(inscriptionSchema) as unknown as Resolver<ValeursInscription>,
    defaultValues: valeursVides,
    mode: 'onTouched',
  });
  const { register, handleSubmit, watch, setValue, setError, reset, formState } = form;
  const { errors, isSubmitting } = formState;
  const nombre = watch('nombre');
  const veutRecevoir = watch('veutRecevoir');
  const dispos = watch('disponibilites');
  const regimes = watch('regimes');

  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        if (mode === 'inscription') {
          const r = await api.get<{ event: EvenementPublic }>(`/api/public/i/${slug}`);
          if (!actif) return;
          setEvent(r.event);
        } else {
          const r = await api.get<{ event: EvenementPublic; foyer: FoyerPublic; modifiable: boolean; raisonBlocage: 'cloture' | 'repartition' | null }>(`/api/public/f/${token}`);
          if (!actif) return;
          setEvent(r.event);
          setModifiable(r.modifiable);
          setRaisonBlocage(r.raisonBlocage);
          reset(depuisFoyer(r.foyer));
        }
        setChargement('ok');
      } catch {
        if (actif) setChargement('introuvable');
      }
    })();
    return () => {
      actif = false;
    };
  }, [mode, slug, token, reset]);

  const phrase = useMemo(() => phraseReception(dispos), [dispos]);

  const basculerRegime = (tag: RegimeTag) => {
    const actuel = regimes.includes(tag) ? regimes.filter((r) => r !== tag) : [...regimes, tag];
    setValue('regimes', actuel, { shouldDirty: true });
    if (tag === 'Allergie' && !regimes.includes(tag)) refPrecisions.current?.focus();
  };

  const onSubmit = handleSubmit(async (valeurs) => {
    setErreurGlobale(null);
    try {
      if (mode === 'inscription') {
        const r = await api.post<{ token: string; lienModification: string; foyer: FoyerPublic; event: EvenementPublic }>(`/api/public/i/${slug}`, valeurs);
        navigate(`/i/${slug}/merci`, { state: { ...r, mode } });
      } else {
        const r = await api.put<{ foyer: FoyerPublic; event: EvenementPublic }>(`/api/public/f/${token}`, valeurs);
        navigate(`/f/${token}/merci`, { state: { ...r, token, mode } });
      }
    } catch (e) {
      if (e instanceof ErreurApi) {
        for (const i of e.issues) setError(i.path as keyof ValeursInscription, { message: i.message });
        if (e.issues.length === 0) setErreurGlobale(e.message);
        else setErreurGlobale('Vérifiez les champs signalés.');
      } else {
        setErreurGlobale('Impossible de joindre le serveur. Réessayez dans un instant.');
      }
    }
  });

  if (chargement === 'en_cours') {
    return (
      <div className="page-mobile">
        <BandeauSignature />
        <p className="texte-muet" style={{ padding: 28 }}>
          Chargement…
        </p>
      </div>
    );
  }
  if (chargement === 'introuvable' || !event) {
    return (
      <div className="page-mobile">
        <BandeauSignature />
        <header style={{ padding: '28px 20px' }} className="pile">
          <h1 className="display">Ce lien ne mène nulle part</h1>
          <p className="texte-doux">{mode === 'inscription' ? "Cet événement n'existe pas ou n'est plus ouvert aux inscriptions." : 'Ce lien de modification est invalide. Reprenez-le dans votre e-mail de confirmation.'}</p>
        </header>
      </div>
    );
  }

  const ferme = mode === 'inscription' ? !event.inscriptionsOuvertes : !modifiable;

  return (
    <div className="page-mobile">
      <BandeauSignature />
      <header style={{ padding: '28px 20px 8px' }} className="pile">
        <Embleme />
        {event.organisation ? <div className="surtitre">{event.organisation}</div> : null}
        <h1 className="display">{mode === 'inscription' ? event.nom : 'Modifier mon inscription'}</h1>
        <p className="texte-doux texte-long">{event.description ?? 'Une entrée, un plat, un dessert : trois tables chez trois Lions différents. À chaque étape, vous changez de maison et de convives.'}</p>
        <div className="carte" style={{ padding: 16, borderRadius: 16 }}>
          <div className="pile" style={{ gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{capitale(event.dateLongue)}</div>
            {ETAPES.map((e) => (
              <div key={e} className="ligne" style={{ fontSize: 15 }}>
                <PastilleEtape etape={e} />
                <span style={{ marginLeft: 'auto', minWidth: 170, textAlign: 'left' }}>{heureCourte(event.horaires[e])}</span>
              </div>
            ))}
            {event.commune ? (
              <div className="ligne" style={{ fontSize: 15 }}>
                <span className="pastille etape-commune">
                  <span className="icone">
                    <IconeCommune />
                  </span>
                  <span className="texte">
                    <span className="libelle">{event.commune.nom}</span>
                  </span>
                </span>
                <span style={{ marginLeft: 'auto', minWidth: 170, textAlign: 'left' }}>{heureCourte(event.commune.heure)}</span>
              </div>
            ) : null}
            <div className="f-hint" style={{ paddingTop: 4 }}>
              {event.inscriptionsOuvertes ? `Inscriptions ouvertes jusqu'au ${dateCourte(event.dateLimiteInscription)}` : event.complet ? 'Événement complet' : `Inscriptions closes depuis le ${dateCourte(event.dateLimiteInscription)}`}
            </div>
          </div>
        </div>
      </header>

      {ferme ? (
        <div style={{ padding: '16px 20px 0' }}>
          <BandeauAlerte niveau="info" titre={raisonBlocage === 'repartition' ? 'Les tables sont faites.' : event.complet ? "L'événement est complet." : 'Les inscriptions sont closes.'}>
            {raisonBlocage === 'repartition'
              ? "Votre parcours est en préparation ou déjà envoyé : votre inscription ne peut plus être modifiée en ligne. Pour un changement, contactez l'organisateur."
              : `Contactez l'organisateur si vous souhaitez ${mode === 'inscription' ? 'participer' : 'modifier votre inscription'}.`}
          </BandeauAlerte>
        </div>
      ) : (
        <form style={{ padding: '16px 20px 0' }} className="pile" onSubmit={onSubmit} noValidate>
          {erreurGlobale ? (
            <BandeauAlerte niveau="attention" role="alert" titre={erreurGlobale} />
          ) : null}

          <section className="carte pile" aria-labelledby="s1">
            <div className="titre-section">
              <span className="sec-num" aria-hidden="true">
                1
              </span>
              <h2 id="s1">Qui vient ?</h2>
            </div>
            <div className="segments" role="group" aria-label="Nombre de personnes">
              {([1, 2, 3] as const).map((n) => (
                <button key={n} type="button" className="seg" aria-pressed={nombre === n} onClick={() => setValue('nombre', n, { shouldDirty: true })}>
                  {n === 1 ? 'Seul·e' : n === 2 ? 'À deux' : 'À trois'}
                </button>
              ))}
            </div>
            <Champ id="personne1" label="Votre prénom et nom" autoComplete="name" erreur={errors.personne1?.message} {...register('personne1')} />
            <Champ id="email1" label="Votre e-mail" optionnel={nombre > 1 ? 'au moins un e-mail dans la voiture' : undefined} type="email" autoComplete="email" inputMode="email" erreur={errors.email1?.message} aide="Chaque personne qui donne son e-mail reçoit le parcours de la soirée." {...register('email1')} />
            {nombre >= 2 ? (
              <>
                <Champ
                  id="personne2"
                  label={
                    <>
                      Prénom et nom de la 2<sup>e</sup> personne
                    </>
                  }
                  erreur={errors.personne2?.message}
                  aide={nombre === 2 ? 'Vous passerez toute la soirée ensemble, dans les mêmes groupes.' : undefined}
                  {...register('personne2')}
                />
                <Champ
                  id="email2"
                  label={
                    <>
                      E-mail de la 2<sup>e</sup> personne
                    </>
                  }
                  optionnel="facultatif"
                  type="email"
                  inputMode="email"
                  erreur={errors.email2?.message}
                  {...register('email2')}
                />
              </>
            ) : null}
            {nombre >= 3 ? (
              <>
                <Champ
                  id="personne3"
                  label={
                    <>
                      Prénom et nom de la 3<sup>e</sup> personne
                    </>
                  }
                  erreur={errors.personne3?.message}
                  aide="Vous passerez toute la soirée ensemble, tous les trois, dans les mêmes groupes."
                  {...register('personne3')}
                />
                <Champ
                  id="email3"
                  label={
                    <>
                      E-mail de la 3<sup>e</sup> personne
                    </>
                  }
                  optionnel="facultatif"
                  type="email"
                  inputMode="email"
                  erreur={errors.email3?.message}
                  {...register('email3')}
                />
              </>
            ) : null}
          </section>

          <section className="carte pile" aria-labelledby="s2">
            <div className="titre-section">
              <span className="sec-num" aria-hidden="true">
                2
              </span>
              <h2 id="s2">Vous joindre</h2>
            </div>
            <Champ id="telephone" label="Téléphone" optionnel="facultatif, pour le jour J" type="tel" autoComplete="tel" inputMode="tel" erreur={errors.telephone?.message} {...register('telephone')} />
          </section>

          <section className="carte pile" aria-labelledby="s3">
            <div className="titre-section">
              <span className="sec-num" aria-hidden="true">
                3
              </span>
              <h2 id="s3">À table</h2>
            </div>
            <div className="f-label" id="regimes-label">
              Régime ou allergies
            </div>
            <div className="etiquettes" role="group" aria-labelledby="regimes-label">
              {REGIMES_TAGS.map((tag) => (
                <button key={tag} type="button" className="tag" aria-pressed={regimes.includes(tag)} onClick={() => basculerRegime(tag)}>
                  {tag === 'Allergie' ? 'Allergie…' : tag}
                </button>
              ))}
            </div>
            <Champ
              id="precisionsRegime"
              label={<span style={{ fontWeight: 500 }}>Précisions</span>}
              optionnel="qui, quoi"
              aide="Vos hôtes en seront informés pour adapter leur menu."
              erreur={errors.precisionsRegime?.message}
              {...register('precisionsRegime')}
              ref={(el) => {
                register('precisionsRegime').ref(el);
                refPrecisions.current = el;
              }}
            />
          </section>

          <section className="carte accent pile" aria-labelledby="s4">
            <div className="titre-section">
              <span className="sec-num" aria-hidden="true">
                4
              </span>
              <h2 id="s4">Acceptez-vous de recevoir ?</h2>
            </div>
            <p className="texte-doux" style={{ fontSize: 15 }}>
              Recevoir, c'est préparer une seule des trois étapes chez vous, pour environ 4 personnes, vous compris.
            </p>
            <div className="segments" role="group" aria-label="Recevoir chez vous">
              <button type="button" className="seg" aria-pressed={veutRecevoir} onClick={() => setValue('veutRecevoir', true, { shouldDirty: true })}>
                <IconeMaison /> Oui, avec plaisir
              </button>
              <button type="button" className="seg" aria-pressed={!veutRecevoir} onClick={() => setValue('veutRecevoir', false, { shouldDirty: true })}>
                Non, cette fois
              </button>
            </div>

            {veutRecevoir ? (
              <fieldset className="bloc-doux" style={{ border: 0, margin: 0 }}>
                <legend className="f-label" style={{ fontSize: 16, padding: 0 }}>
                  Quelle étape pourriez-vous recevoir ?
                </legend>
                <div style={{ fontSize: 14, lineHeight: 1.5 }} className="texte-doux">
                  <strong>Cochez tout ce qui vous convient : vous ne recevrez qu'une seule fois.</strong> Plus vous cochez, plus il est facile d'équilibrer la soirée.
                </div>
                {ETAPES.map((e) => {
                  const coche = dispos.includes(e);
                  return (
                    <label key={e} className={`choix-etape etape-${e}${coche ? ' coche' : ''}`}>
                      <input type="checkbox" value={e} {...register('disponibilites')} />
                      <span className="icone">
                        <IconeEtape etape={e} />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column' }}>
                        <span className="libelle">{ETAPE_INFO[e].label}</span>
                        <span className="heure">{plageEtape(event.horaires, e)}</span>
                      </span>
                    </label>
                  );
                })}
                {errors.disponibilites?.message ? (
                  <span className="f-erreur" role="alert">
                    {errors.disponibilites.message}
                  </span>
                ) : null}
                <div className="pile-6">
                  <label className="f-label" htmlFor="adresse" style={{ fontSize: 16 }}>
                    Votre adresse
                  </label>
                  <input id="adresse" className="f-input" autoComplete="street-address" aria-invalid={errors.adresse ? 'true' : undefined} {...register('adresse')} />
                  <input className="f-input" aria-label="Complément d'adresse" placeholder="Étage, code, interphone…" {...register('complementAdresse')} />
                  {errors.adresse?.message ? (
                    <span className="f-erreur" role="alert">
                      {errors.adresse.message}
                    </span>
                  ) : null}
                  <span className="f-hint">Transmise uniquement aux convives qui viendront chez vous, le soir même.</span>
                </div>
                <div className="pile-6">
                  <label className="f-label" htmlFor="capaciteMax" style={{ fontSize: 16 }}>
                    Combien de personnes au maximum à votre table ?
                  </label>
                  <select id="capaciteMax" className="f-input" aria-describedby="capaciteMax-aide" {...register('capaciteMax', { setValueAs: (v) => (v === '' ? undefined : Number(v)) })}>
                    {[3, 4, 5, 6, 7, 8, 9, 10, 12].map((n) => (
                      <option key={n} value={n}>
                        {n} personnes
                      </option>
                    ))}
                  </select>
                  <span id="capaciteMax-aide" className="f-hint">
                    En vous comptant{nombre === 2 ? ' tous les deux' : nombre === 3 ? ' tous les trois' : ''}. L'organisateur ne placera jamais plus de monde chez vous.
                  </span>
                  {errors.capaciteMax?.message ? (
                    <span className="f-erreur" role="alert">
                      {errors.capaciteMax.message}
                    </span>
                  ) : null}
                </div>
                <div className="encart-pointille" aria-live="polite">
                  <span style={{ flex: 'none', marginTop: 1 }}>
                    <IconeMaison taille={20} />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: phrase.replace('une seule fois', '<strong>une seule fois</strong>').replace(' ou ', ' <strong>ou</strong> ') }} />
                </div>
              </fieldset>
            ) : null}
          </section>

          <button type="submit" className="btn action" disabled={isSubmitting}>
            {mode === 'inscription' ? "Je m'inscris" : 'Enregistrer mes modifications'}
          </button>
          <p className="f-hint" style={{ textAlign: 'center' }}>
            Vous pourrez modifier votre inscription jusqu'au {dateCourte(event.dateLimiteInscription)}.
          </p>
        </form>
      )}
    </div>
  );
}
